import React, { useMemo } from 'react';

import { Box, SidePage, Section, Typography } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import {
  FinancialResultFormDuplicate,
  FinancialResultFormEdit,
} from 'scenes/FinancialResults/components/FinancialResultForm';
import FinancialResultTitle from 'scenes/FinancialResults/components/FinancialResultTitle';
import {
  TAB_DETAILS,
  TAB_INVOICES,
  TAB_ACTIONS_LOG,
  STATUS_DRAFT,
} from 'scenes/FinancialResults/constants';
import { useLoadFinancialResultsDetails } from 'scenes/FinancialResults/data';
import { exportAttachment } from 'scenes/FinancialResults/sources/financialResults';
import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import BusinessPartnerDetails from 'scenes/BusinessPartners/components/BusinessPartnerDetails';
import SpendAdjustmentDetails from 'scenes/SpendAdjustments/components/SpendAdjustmentDetails';
import InvoiceSectionOnWait from 'scenes/FinancialResults/components/Draft/InvoiceSectionOnDraft';
import AlertOnWait from 'scenes/FinancialResults/components/Draft/AlertOnDraft';

import FrActions from '../Actions';
import ActionsLog from './ActionsLog';
import FinancialResultChart from './FinancialResultChart';
import GeneralSection from './GeneralSection';
import IOS from './IOS';
import Invoices from './Invoices';
import Metadata from './Metadata';
import { SpendAdjustments } from './SpendAdjustments';
import Stats from './Stats';
import IODetails from 'scenes/IO/components/IODetails';
import GeneralSectionOnDraft from 'scenes/FinancialResults/components/Draft/GeneralSectionOnDraft';

const idOfBPLevel = 0;

const FinancialResultDetails = ({
  id,
  activeTabId = TAB_DETAILS,
  onClose,
  onChange,
  onExited,
  open,
}) => {
  const { data, isLoading, isFetching } = useLoadFinancialResultsDetails(id);

  const [childComponent, openChildComponent] = useChildComponent();

  const isBusinessPartner = useMemo(
    () => data?.businessEntities?.[0]?.level === idOfBPLevel,
    [data?.businessEntities?.[0]?.level, idOfBPLevel]
  );
  const hasSpendAdjustments = useMemo(
    () => !!data?.spendAdjustments.length,
    [data?.spendAdjustments]
  );
  const handleEdit = (data) => {
    openChildComponent(FinancialResultFormEdit, {
      id: data.id,
    });
  };

  const handleDuplicate = (data) => {
    openChildComponent(FinancialResultFormDuplicate, {
      id: data.id,
    });
  };

  const handleDelete = async () => {
    onClose();
  };

  const handleExportAttachment = async (id) => {
    await exportAttachment({ id });
  };

  const handleOpenBusinessPartner = (_, { id }) => {
    openChildComponent(BusinessPartnerDetails, { id });
  };

  const handleOpenAffiliate = (_, { id }) => {
    openChildComponent(AffiliateDetails, { id, onChange });
  };

  const handleOpenSpendAdjustmentDetails = (_, { id }) => {
    openChildComponent(SpendAdjustmentDetails, {
      id,
    });
  };
  const handleOpenIo = (_, { id }) => {
    openChildComponent(IODetails, {
      id: id,
    });
  };

  const isBudgetOverrun =
    data?.totalInvoicesAmount?.amount > data?.budget?.amount;
  const isDraftDetails = data ? data?.status?.id === STATUS_DRAFT : true;

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading}
      fetching={isFetching}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={`Financial Result`}
        entityId={id}
        subTitle={data ? <FinancialResultTitle {...data} /> : null}
      />
      <SidePage.Tabs activeTabId={activeTabId}>
        <SidePage.Tab label={TAB_DETAILS}>
          <SidePage.NewBody wrapper={true} dense={true}>
            {data && (
              <React.Fragment>
                <SidePage.NewContent withSidebar={true}>
                  {isDraftDetails && (
                    <Box sx={{ mt: 4 }}>
                      <AlertOnWait />
                    </Box>
                  )}

                  <Section title={'General'}>
                    {isDraftDetails ? (
                      <GeneralSectionOnDraft
                        data={data}
                        onOpenBusinessPartner={handleOpenBusinessPartner}
                      />
                    ) : (
                      <GeneralSection
                        data={data}
                        onOpenBusinessPartner={handleOpenBusinessPartner}
                        onOpenAffiliate={handleOpenAffiliate}
                        onExportAttachment={handleExportAttachment}
                        onOpenIo={handleOpenIo}
                      />
                    )}
                  </Section>
                  {isDraftDetails && (
                    <Section title={'Invoice'}>
                      <InvoiceSectionOnWait
                        invoices={data?.invoices}
                        senderEmail={data?.senderEmail}
                      />
                    </Section>
                  )}

                  {!isDraftDetails && (
                    <Section
                      title={`${
                        isBusinessPartner
                          ? 'Business Partner'
                          : `Affiliate${
                              data?.businessEntities?.length > 1 ? 's' : ''
                            }`
                      } Activity`}
                      isCollapsable
                      initialOpen={false}
                    >
                      <FinancialResultChart id={data?.id} />
                    </Section>
                  )}

                  {!isDraftDetails && !!data?.spend?.io.length && (
                    <Section
                      title={'Current IOs'}
                      subTitle={
                        (isBudgetOverrun ||
                          !!data?.spend?.ioDiscrepancyDetails) &&
                        !data?.spend?.skipSpendCheck ? (
                          <Typography
                            color={'error'}
                            variant={'inherit'}
                            component={'span'}
                          >
                            To resolve the amount discrepancy, add an Amendment
                            with the correct budget - it will <b>replace</b>{' '}
                            IO's budget for the same period.
                          </Typography>
                        ) : (
                          ''
                        )
                      }
                      isCollapsable
                    >
                      <IOS data={data?.spend?.io} />
                    </Section>
                  )}

                  {!isDraftDetails && !!data?.stats && (
                    <Section
                      title={'Summary by Applications'}
                      subTitle={
                        'UA / RE / Total rejected installs by post-attribution fraud had been attributed to the affiliate, however they were reported as fraud.'
                      }
                      isCollapsable
                    >
                      <Stats
                        stats={data?.stats}
                        businessEntities={data?.businessEntities}
                      />
                    </Section>
                  )}

                  {!isDraftDetails && hasSpendAdjustments && (
                    <Section title={'Spend Adjustments'} isCollapsable>
                      <SpendAdjustments
                        data={data?.spendAdjustments}
                        onOpenSpendAdjustment={handleOpenSpendAdjustmentDetails}
                      />
                    </Section>
                  )}
                </SidePage.NewContent>

                <SidePage.Sidebar
                  actions={
                    <FrActions
                      data={data}
                      actions={data?.actions}
                      onEdit={handleEdit}
                      onDuplicate={handleDuplicate}
                      onDelete={handleDelete}
                      showDraftAlert={isDraftDetails}
                    />
                  }
                >
                  <Metadata data={data} />
                </SidePage.Sidebar>
              </React.Fragment>
            )}
          </SidePage.NewBody>
        </SidePage.Tab>
        {!!data?.invoices?.length && !isDraftDetails ? (
          <SidePage.Tab label={TAB_INVOICES} count={data?.invoices?.length}>
            <SidePage.NewBody>
              <Invoices
                invoices={data?.invoices}
                totalAmount={data?.totalInvoicesAmount}
              />
            </SidePage.NewBody>
          </SidePage.Tab>
        ) : null}
        {!!data?.actionsLog?.length && (
          <SidePage.Tab label={TAB_ACTIONS_LOG}>
            <SidePage.NewBody>
              <ActionsLog logs={data?.actionsLog} />
            </SidePage.NewBody>
          </SidePage.Tab>
        )}
      </SidePage.Tabs>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default FinancialResultDetails;
